
































































import { Component, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderGetters } from "@/store/modules/order/types";
import { Order } from "@/models/order/order";
import { Mobile } from "@/mixins/mobile";
import { SurchargeStore } from "@/mixins/surcharge";
import EditMultiplier from "@/components/elements/utility/EditMultiplier.vue";
import MultiplierWarning from "@/components/elements/utility/MultiplierWarning.vue";
@Component({ components: { EditMultiplier, MultiplierWarning } })
export default class OrderSummary extends Mixins(Mobile, SurchargeStore) {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;

  // NOTE: this component used to clear `created_type` on the cart page. That
  // erased the "duplicated" signal before checkout could read it, so a
  // duplicate's ingested notes got wiped by CheckoutForm's new-quote reset.
  // The flag is frontend-only and reset by `clearOrder` on any new-quote/load,
  // so it is now left in place until then. See CheckoutForm.initializeFromCustomer.

  /** FUMBSD-761: rate and label come from the API (GET /prices/surcharge) */
  get surchargeDecimal(): number {
    return this.surcharge.rate > 0 ? this.surcharge.rate : 0;
  }

  get surchargeLabel(): string {
    return this.surcharge.label;
  }

  get surchargePercent(): number {
    return this.surchargeDecimal > 0 ? this.surchargeDecimal * 100 : 0;
  }

  get price(): number {
    let price = 0;
    for (const item of this.storeOrder.items!) {
      price += item.qty * (item.unit_price * item.multiplier);
    }
    return price;
  }

  get surchargeAmount(): number {
    if (this.surchargePercent <= 0) return 0;
    return parseFloat((this.price * this.surchargePercent / 100).toFixed(2));
  }

  /**
   * FUPOF-209: shipping and tax as NetSuite holds them on the quote, landed by the
   * reload when a submitted quote is reopened (`reloadFromNs`). Neither is ours to
   * calculate — Frankford keys freight in after a rep requests a shipping quote, and
   * tax is AvaTax's — and neither comes back as a cart line, so before this the
   * summary could only ever say TBD.
   *
   * Null means unknown: a draft never submitted, an older API, or a reload whose
   * NetSuite read failed. **Zero is shown as TBD too**, for both amounts, and that is
   * a deliberate business decision (Frankford, 2026-09-11) rather than an oversight —
   * so please don't "fix" it:
   *
   *   Shipping — the data agrees it is unpriced. Of 380 Production estimates since
   *   June 2026, 346 carry priced freight and only 34 read 0 (11 with a zero ShipItem
   *   line, 23 with none). "$0.00 Shipping" would tell a rep freight is free when it
   *   has simply not been quoted yet.
   *
   *   Tax — the data actually says the opposite, and TBD is still what we want. 193 of
   *   those 380 estimates carry a TaxItem line at exactly 0, and those zeros are real:
   *   they track no-nexus states (DE 0/15 taxed, MS 0/8, NE, OR, MT, NH, CT) and
   *   per-customer resale exemption inside taxable ones (CA 46/106, FL 48/93). AvaTax
   *   does compute on estimates. But a quote is not a sales order: tax is not locked
   *   until the SO is generated, and exemption lives in Avalara/CertCapture, not on
   *   the NetSuite customer (`taxable = 'F'` is set on 1 customer of 5,600; the
   *   `custentity_ava_*` exemption fields do not exist in the account; customers
   *   flagged "-Not Taxable-" were still taxed on 3 of 11 recent estimates). So a $0
   *   is treated as a prompt for Frankford to confirm exemption, not as an answer to
   *   show the customer. Note this makes the cart read TBD where the NetSuite quote
   *   PDF and the rep's own quote email print "Tax: $0.00" — accepted trade-off.
   */
  get shippingCost(): number | null {
    return this.knownAmount(this.storeOrder.ns_totals?.shipping);
  }

  get taxAmount(): number | null {
    return this.knownAmount(this.storeOrder.ns_totals?.tax);
  }

  private knownAmount(value: number | undefined): number | null {
    return typeof value === "number" && value > 0 ? value : null;
  }

  /**
   * Subtotal and surcharge stay this app's own numbers — the cart is the authority
   * while the rep is editing, and NetSuite's subtotal has the surcharge line baked
   * in, so taking it as well would count the surcharge twice. Only the two amounts
   * the cart cannot know are added from NetSuite.
   */
  get totalPrice(): number {
    return (
      this.price +
      this.surchargeAmount +
      (this.shippingCost ?? 0) +
      (this.taxAmount ?? 0)
    );
  }

  get totalQty(): number {
    let qty = 0;
    if (this.storeOrder.items && this.storeOrder.items.length) {
      for (const item of this.storeOrder.items) {
        qty += item.qty;
      }
    }
    return qty;
  }

  get isReviewPage(): boolean {
    return this.$route.name === "OrderReview";
  }

  /**
   * The cart is where multipliers are edited, so the warning's link is dropped
   * there. Covers the saved-cart route too — same view, same reason.
   */
  get isCartPage(): boolean {
    return this.$route.name === "Cart" || this.$route.name === "SavedCart";
  }
}
